import { useAtom } from "jotai";
import React from "react";
import { useTranslation } from "react-i18next";
import { volumeAtom } from "~/Atom";
import { PlaySound } from "~/SoundUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import "~/css/SystemSettings.css";
import {
  useLocationSettings,
  VALIDATE_STATES,
} from "~/components/settings/HamburgerMenu/hooks/useLocationSettings";
import { useLogoSettings } from "~/components/settings/HamburgerMenu/hooks/useLogoSettings";
import { useCustomVariables } from "~/components/settings/HamburgerMenu/hooks/useCustomVariables";
import { useIsAwsChatPage } from "~/utils/envUtils";

/**
 * 場所情報セクションのコンポーネント
 */
const LocationSection: React.FC<{
  locationSettings: ReturnType<typeof useLocationSettings>;
  greeting: ReturnType<typeof useCustomVariables>["greeting"];
  t: (key: string) => string;
  volume: number;
}> = ({ locationSettings, greeting, t, volume }) => (
  <>
    <div className="title font-default-normal-20 location-title">{t("locationInformation")}</div>
    <div className="location-container">
      <span className="location-label">{t("exampleSentence")}</span>
      <span className="location-content">{greeting({ lng: "ja-JP" })}</span>
    </div>
    <div className="location-container">
      <span className="location-label">{t("exampleSentenceEn")}</span>
      <span className="location-content">{greeting({ lng: "en-US" })}</span>
    </div>
    <div className="location-container">
      <span className="location-label">{t("japaneseName")}</span>
      <div className="location-content">
        <input
          type="text"
          value={locationSettings.locationInputJA}
          onChange={locationSettings.handleLocationChangeJA}
          placeholder={t("cityHall")}
        />
      </div>
    </div>
    <div className="location-container">
      <span className="location-label">{t("englishName")}</span>
      <div className="location-content">
        <input
          type="text"
          value={locationSettings.locationInputEN}
          onChange={locationSettings.handleLocationChangeEN}
          placeholder={t("cityHallEn")}
        />
      </div>
    </div>
    <div className="location-container">
      <span className="location-label">{t("addressSearch")}</span>
      <div className="location-content">
        <input
          type="text"
          className="location-input-address"
          value={locationSettings.locationInputSearchAddress}
          onChange={locationSettings.handleLocationChangeSearchAddress}
          placeholder={t("pleaseEnterYourPostalCodeOrAddress")}
        />
        <button
          className="button-0 location-search-button"
          onClick={locationSettings.handleSearchClick}
          onMouseUp={() => PlaySound("cancel", volume)}
          disabled={locationSettings.isSearchingDisabled}
        >
          {locationSettings.isSearching ? <div className="loading-spinner"></div> : t("search")}
        </button>
        <span
          className={`search-validated ${locationSettings.searchValidation.state === VALIDATE_STATES.INFO ? "info-color" : "error-color"}`}
        >
          {locationSettings.searchValidation.message}
        </span>
      </div>
    </div>
    <div className="location-container">
      <span className="location-label">{t("address")}</span>
      <div className="location-content">
        <input
          type="text"
          value={locationSettings.locationInputAddress}
          className="location-input-address"
          readOnly
        />
      </div>
    </div>
    <div className="validate-container">
      <span
        className={`save-validated ${locationSettings.saveValidation.state === VALIDATE_STATES.INFO ? "info-color" : "error-color"}`}
      >
        {locationSettings.saveValidation.message}
      </span>
    </div>
  </>
);

/**
 * ロゴセクションのコンポーネント
 */
const LogoSection: React.FC<{
  logoSettings: ReturnType<typeof useLogoSettings>;
  t: (key: string) => string;
  volume: number;
  isAwsChatPage: boolean;
  handleSaveSuccess: () => void;
}> = ({ logoSettings, t, volume, isAwsChatPage, handleSaveSuccess }) => (
  <>
    <div className="title font-default-normal-20 location-title">{t("logo")}</div>
    <div className="img-container">
      <img
        src={logoSettings.displayLogo}
        alt=""
        className="logo"
        onClick={logoSettings.handleLogoClick}
      />
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={logoSettings.fileInputRef}
        onChange={logoSettings.handleFileChange}
        disabled={isAwsChatPage}
      />
    </div>
    <div className="validate-container">
      <span className="save-validated info-color">
        {logoSettings.isNotSaved && <>{t("notSaved")}</>}
      </span>
    </div>
    <div className="flex-row justify-content-end">
      <button
        data-tooltip-id="tip_reset"
        className={`button-0 ${isAwsChatPage ? "disabled-btn" : ""}`}
        onClick={logoSettings.resetLogo}
        onMouseUp={() => PlaySound("cancel", volume)}
        disabled={isAwsChatPage}
      >
        {t("reset")}
      </button>
      <button
        data-tooltip-id="tip_save"
        className={`change-logo-button ${isAwsChatPage ? "disabled-btn" : ""}`}
        onClick={logoSettings.handleLogoClick}
        onMouseUp={() => PlaySound("push3", volume)}
        disabled={isAwsChatPage}
      >
        {t("change")}
      </button>
      <button
        data-tooltip-id="tip_save"
        className={`button-1 ${isAwsChatPage ? "disabled-btn" : ""}`}
        onClick={() => logoSettings.saveLogo(handleSaveSuccess)}
        onMouseUp={() => PlaySound("push3", volume)}
        disabled={isAwsChatPage}
      >
        {t("save")}
      </button>
    </div>
  </>
);

const SystemSettings: React.FC = () => {
  const { t } = useTranslation();
  const [volume] = useAtom(volumeAtom);
  // 場所情報とロゴのhooksを使用
  const locationSettings = useLocationSettings();
  const logoSettings = useLogoSettings();
  // 挨拶を取得
  const { greeting } = useCustomVariables({
    jaName: locationSettings.locationInputJA,
    enName: locationSettings.locationInputEN,
  });

  const handleSaveSuccess = () => showNotificationDialog(t("saved"), "success");
  const isAwsChatPage = useIsAwsChatPage();

  return (
    <>
      <LocationSection
        locationSettings={locationSettings}
        greeting={greeting}
        t={t}
        volume={volume}
      />
      <div className="flex-row justify-content-end">
        <button
          data-tooltip-id="tip_reset"
          className="button-0"
          onClick={locationSettings.resetLocation}
          onMouseUp={() => PlaySound("cancel", volume)}
        >
          {t("reset")}
        </button>
        <button
          data-tooltip-id="tip_save"
          className="button-1"
          onClick={() => locationSettings.saveLocation(handleSaveSuccess)}
          onMouseUp={() => PlaySound("push3", volume)}
        >
          {t("save")}
        </button>
      </div>

      <LogoSection
        logoSettings={logoSettings}
        t={t}
        volume={volume}
        isAwsChatPage={isAwsChatPage}
        handleSaveSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default SystemSettings;
